import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Download, Upload, Send, Loader2, FileText, Clock,
  CheckCircle, AlertCircle, XCircle, Paperclip, Banknote, User, Car, Cpu,
  Brain, Zap, ExternalLink, Lock, Unlock, Trash2, Database,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = {
  pending_review: { label: 'Pendiente de revisión', color: 'bg-yellow-500', icon: Clock },
  needs_info: { label: 'Falta información', color: 'bg-orange-500', icon: AlertCircle },
  accepted: { label: 'Aceptada / En cola', color: 'bg-blue-500', icon: CheckCircle },
  in_progress: { label: 'En proceso', color: 'bg-purple-500', icon: Loader2 },
  ready_to_pay: { label: 'Listo para pagar', color: 'bg-emerald-500', icon: Banknote },
  delivered: { label: 'Entregada', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Cerrada', color: 'bg-zinc-500', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'bg-red-500', icon: XCircle },
};
const PRIORITIES = { low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };

const SaveToSolutionsPanel = ({ ticket, token, originals, originalDecrypted, modifieds }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedOri, setSelectedOri] = useState('');
  const [selectedMod, setSelectedMod] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  // Auto-select best candidates
  const allOris = [...originalDecrypted, ...originals];
  const defaultOri = allOris.length > 0 ? allOris[allOris.length - 1].id : '';
  const defaultMod = modifieds.length > 0 ? modifieds[modifieds.length - 1].id : '';

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/tickets/${ticket.id}/save-to-solutions`, {
        ori_file_id: selectedOri || defaultOri,
        mod_file_id: selectedMod || defaultMod,
      }, { headers });
      toast.success(res.data.message);
      setSaved(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar en soluciones');
    }
    setSaving(false);
  };

  if (saved) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4 text-center" data-testid="saved-to-solutions">
        <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
        <p className="text-green-400 text-sm font-heading font-bold">GUARDADO EN SOLUCIONES</p>
      </div>
    );
  }

  return (
    <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-4" data-testid="save-to-solutions-panel">
      <div className="flex items-center gap-2 mb-3">
        <Database className="w-4 h-4 text-emerald-400" />
        <h3 className="font-heading text-sm text-emerald-400 uppercase tracking-wider">Guardar en Soluciones Auto</h3>
      </div>
      <p className="text-zinc-400 text-xs mb-3">Guarda ORI + MOD en la base de datos para que futuros clientes lo encuentren automáticamente.</p>

      {allOris.length > 1 && (
        <div className="mb-2">
          <label className="text-zinc-500 text-xs">ORI:</label>
          <select value={selectedOri || defaultOri} onChange={e => setSelectedOri(e.target.value)}
            className="w-full bg-surface border border-white/10 rounded text-white text-xs p-1.5 mt-1">
            {allOris.map(f => (
              <option key={f.id} value={f.id}>{f.filename} ({f.type})</option>
            ))}
          </select>
        </div>
      )}
      {modifieds.length > 1 && (
        <div className="mb-2">
          <label className="text-zinc-500 text-xs">MOD:</label>
          <select value={selectedMod || defaultMod} onChange={e => setSelectedMod(e.target.value)}
            className="w-full bg-surface border border-white/10 rounded text-white text-xs p-1.5 mt-1">
            {modifieds.map(f => (
              <option key={f.id} value={f.id}>{f.filename} (v{f.version})</option>
            ))}
          </select>
        </div>
      )}

      <div className="bg-surface rounded p-2 mb-3 text-xs">
        <p className="text-zinc-300">ORI: <span className="text-white font-mono">{(allOris.find(f => f.id === (selectedOri || defaultOri)) || allOris[allOris.length-1])?.filename || '?'}</span></p>
        <p className="text-zinc-300">MOD: <span className="text-white font-mono">{(modifieds.find(f => f.id === (selectedMod || defaultMod)) || modifieds[modifieds.length-1])?.filename || '?'}</span></p>
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-to-solutions-btn">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Guardando y analizando...</> : <><Database className="w-4 h-4 mr-2" />Guardar ORI + MOD en Soluciones</>}
      </Button>
    </div>
  );
};

const AdminTicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [modFile, setModFile] = useState(null);
  const [modNotes, setModNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [readingType, setReadingType] = useState('master');
  const [slaveSn, setSlaveSn] = useState('');
  const [updatingReadingType, setUpdatingReadingType] = useState(false);
  const [slaveTools, setSlaveTools] = useState([]);
  const chatRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTicket(); }, [ticketId]);

  // Auto-refresh cada 10s para ver cambios en tiempo real
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/admin/tickets/${ticketId}`, { headers })
        .then(res => {
          setTicket(prev => {
            if (prev && prev.status !== res.data.status) {
              toast.info(`Estado actualizado: ${STATES[res.data.status]?.label || res.data.status}`);
            }
            return res.data;
          });
        }).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [ticketId, token]);

  const fetchTicket = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}`, { headers });
      setTicket(res.data);
      setNewStatus(res.data.status);
      setNewPrice((res.data.price || res.data.service_base_price || '').toString());
      setNewPriority(res.data.priority || 'normal');
      setReadingType(res.data.reading_type || 'master');
      setSlaveSn(res.data.slave_machine_sn || '');
      // Fetch slave tools for the ticket's user
      if (res.data.user_id) {
        axios.get(`${API}/mms/user-tools`, { headers })
          .then(r => {
            const userTools = (r.data.tools || []).filter(t => t.user_id === res.data.user_id);
            setSlaveTools(userTools);
          }).catch(() => {});
      }
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  };

  const updateStatus = async () => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/status`, { status: newStatus }, { headers });
      toast.success('Estado actualizado');
      await fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updatePrice = async () => {
    if (!newPrice || isNaN(newPrice)) return toast.error('Precio inválido');
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/price`, { price: parseFloat(newPrice), estimated_time: estimatedTime || null }, { headers });
      toast.success('Precio asignado');
      await fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updatePriority = async () => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/priority?priority=${newPriority}`, {}, { headers });
      toast.success('Prioridad actualizada');
      await fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateReadingType = async () => {
    setUpdatingReadingType(true);
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/reading-type`, {
        reading_type: readingType,
        slave_machine_sn: readingType === 'slave' ? slaveSn : null,
      }, { headers });
      toast.success(`Tipo de lectura: ${readingType.toUpperCase()}`);
      await fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setUpdatingReadingType(false); }
  };

  const uploadModified = async () => {
    if (!modFile) return toast.error('Selecciona un archivo');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', modFile);
      fd.append('notes', modNotes || '');
      await axios.post(`${API}/admin/tickets/${ticketId}/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Archivo subido');
      setModFile(null); setModNotes('');
      await fetchTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir'); }
    finally { setUploading(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() && !attachment) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', message);
      if (attachment) fd.append('attachment', attachment);
      await axios.post(`${API}/tickets/${ticketId}/messages`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setMessage(''); setAttachment(null);
      await fetchTicket();
      setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
    } catch { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const handleDownload = async (fileVersion) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}/download/${fileVersion.id}`, { headers, responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = fileVersion.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      try { const t = await err.response?.data?.text(); const j = JSON.parse(t); toast.error(j.detail || 'Error al descargar'); }
      catch { toast.error(`Error al descargar (${err.response?.status || 'red'})`); }
    }
  };

  const fetchAiSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}/ai-suggestions`, { headers });
      setAiSuggestions(res.data);
      if (res.data.suggestions?.length > 0) {
        toast.success(`${res.data.suggestions.length} sugerencia(s) encontrada(s)`);
      } else {
        toast.info(res.data.message || 'No se encontraron coincidencias');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en análisis IA');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>;
  if (!ticket) return <Layout><p className="text-zinc-400 text-center py-20">No encontrada</p></Layout>;

  const stateInfo = STATES[ticket.status] || STATES.pending_review;
  const originals = ticket.file_versions?.filter(v => v.type === 'original') || [];
  const originalDecrypted = ticket.file_versions?.filter(v => v.type === 'original_decrypted') || [];
  const modifieds = ticket.file_versions?.filter(v => v.type === 'modified') || [];
  const isPaid = ticket.payment_status === 'paid';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="admin-ticket-detail">
        <div className="flex items-start justify-between mb-6">
          <div>
            <button onClick={() => navigate('/admin/tickets')} className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3 h-3" />Volver a solicitudes
            </button>
            <h1 className="font-heading text-2xl font-bold text-white">{ticket.ticket_number}</h1>
            <p className="text-zinc-400 mt-1">{ticket.vehicle_brand} {ticket.vehicle_model} ({ticket.vehicle_year})</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-white ${stateInfo.color}`}>
            <stateInfo.icon className="w-4 h-4" />{stateInfo.label}
          </div>
          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={async () => {
              if (!window.confirm(`¿Eliminar ticket ${ticket.ticket_number}? Esta acción no se puede deshacer.`)) return;
              try {
                await axios.delete(`${API}/admin/tickets/${ticketId}`, { headers });
                toast.success('Ticket eliminado');
                navigate('/admin/tickets');
              } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
            }}
            data-testid="delete-ticket-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Vehicle & Service Info */}
            <div className="bg-surface border border-white/10 rounded-md p-5">
              <div className="flex items-center gap-2 mb-4">
                <Car className="w-4 h-4 text-primary" />
                <h3 className="font-heading text-white font-bold text-sm">DATOS DEL VEHÍCULO</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><span className="text-zinc-500">Marca</span><p className="text-white">{ticket.vehicle_brand}</p></div>
                <div><span className="text-zinc-500">Modelo</span><p className="text-white">{ticket.vehicle_model}</p></div>
                <div><span className="text-zinc-500">Año</span><p className="text-white">{ticket.vehicle_year}</p></div>
                <div><span className="text-zinc-500">ECU</span><p className="text-white">{ticket.ecu_type}</p></div>
                <div><span className="text-zinc-500">Unidad</span><p className="text-white">{ticket.unit_type?.toUpperCase()}</p></div>
                <div><span className="text-zinc-500">Herramienta</span><p className="text-white">{ticket.reading_tool}</p></div>
                <div><span className="text-zinc-500">Matrícula</span><p className="text-white font-mono">{ticket.license_plate}</p></div>
                {ticket.vin && <div><span className="text-zinc-500">VIN</span><p className="text-white font-mono text-xs">{ticket.vin}</p></div>}
                {ticket.transmission && <div><span className="text-zinc-500">Transmisión</span><p className="text-white">{ticket.transmission === 'manual' ? 'Manual' : 'Automático'}</p></div>}
                {ticket.vehicle_type && <div><span className="text-zinc-500">Tipo</span><p className={`font-medium ${ticket.vehicle_type === 'industrial' ? 'text-orange-400' : 'text-white'}`}>{ticket.vehicle_type === 'industrial' ? 'Industrial' : 'Turismo'}</p></div>}
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-primary" />
                <span className="text-zinc-500 text-sm">Servicio:</span>
                <span className="text-white text-sm font-medium">{ticket.service_name}</span>
                <span className="text-zinc-500 text-sm">| Precio base: {ticket.service_base_price}€</span>
              </div>
              {ticket.notes && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-zinc-500 text-xs mb-1">Notas del cliente:</p>
                  <p className="text-zinc-300 text-sm">{ticket.notes}</p>
                </div>
              )}
            </div>

            {/* Files (moved from right panel) */}
            <div className="bg-surface border border-white/10 rounded-md p-4">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Archivos</h3>
              <div className="space-y-2 mb-4">
                {originals.map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                    className="w-full flex items-center gap-2 p-2 rounded bg-surface-highlight hover:bg-white/10 transition-colors text-left">
                    <Upload className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-white text-xs truncate">{f.filename}</p><p className="text-zinc-600 text-[10px]">Original {ticket.reading_type === 'slave' ? '(encriptado)' : ''}</p></div>
                    <Download className="w-3 h-3 text-zinc-500" />
                  </button>
                ))}
                {originalDecrypted.map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                    className="w-full flex items-center gap-2 p-2 rounded bg-cyan-500/5 border border-cyan-500/20 hover:bg-cyan-500/10 transition-colors text-left">
                    <Unlock className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-white text-xs truncate">{f.filename}</p><p className="text-zinc-600 text-[10px]">.bin desencriptado{f.notes && ` - ${f.notes}`}</p></div>
                    <Download className="w-3 h-3 text-cyan-400" />
                  </button>
                ))}
                {modifieds.map(f => {
                  const hasEncrypted = ticket.file_versions?.some(v => v.type === 'modified_encrypted' && v.version === f.version);
                  const isSlave = ticket.reading_type === 'slave';
                  return (
                    <div key={f.id} className="flex items-center gap-1">
                      <button onClick={() => handleDownload(f)}
                        className="flex-1 flex items-center gap-2 p-2 rounded bg-green-500/5 border border-green-500/20 hover:bg-green-500/10 transition-colors text-left">
                        <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0"><p className="text-white text-xs truncate">{f.filename}</p><p className="text-zinc-600 text-[10px]">Mod v{f.version}{f.notes && ` - ${f.notes}`}</p></div>
                        <Download className="w-3 h-3 text-green-400" />
                      </button>
                      {isSlave && !hasEncrypted && (
                        <Button size="sm" variant="ghost" className="text-orange-400 hover:bg-orange-500/10 flex-shrink-0 px-2" title="Encriptar para SLAVE"
                          onClick={async () => { const sn = ticket.slave_machine_sn; if (!sn) { toast.error('Asigna una herramienta SLAVE primero'); return; } try { toast.info('Encriptando...'); const res = await axios.post(`${API}/admin/tickets/${ticketId}/encrypt/${f.id}`, { machine_sn: sn }, { headers }); toast.success(res.data.message); fetchTicket(); } catch (e) { toast.error(e.response?.data?.detail || 'Error al encriptar'); } }}>
                          <Lock className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {ticket.file_versions?.filter(v => v.type === 'modified_encrypted').map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                    className="w-full flex items-center gap-2 p-2 rounded bg-orange-500/5 border border-orange-500/20 hover:bg-orange-500/10 transition-colors text-left">
                    <Lock className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-white text-xs truncate">{f.filename}</p><p className="text-zinc-600 text-[10px]">Encriptado v{f.version}{f.notes && ` - ${f.notes}`}</p></div>
                    <Download className="w-3 h-3 text-orange-400" />
                  </button>
                ))}
              </div>
              <div className="border-t border-white/10 pt-3">
                <p className="text-zinc-400 text-xs mb-2">Subir archivo modificado:</p>
                <input type="file" className="hidden" id="mod-file" onChange={e => setModFile(e.target.files?.[0])} />
                <Button type="button" variant="outline" size="sm" className="w-full border-white/10 text-white mb-2"
                  onClick={() => document.getElementById('mod-file').click()}>
                  <Upload className="w-3 h-3 mr-2" />{modFile ? modFile.name : 'Seleccionar archivo'}
                </Button>
                {modFile && (
                  <>
                    <Input value={modNotes} onChange={e => setModNotes(e.target.value)} placeholder="Notas de flasheo..."
                      className="bg-surface-highlight border-white/10 text-white text-xs mb-2" />
                    <Button onClick={uploadModified} disabled={uploading} size="sm" className="w-full bg-green-600 hover:bg-green-700">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                      Subir Modificado
                    </Button>
                  </>
                )}
              </div>
              {ticket.reading_type === 'slave' && (
                <div className="border-t border-orange-500/20 pt-3">
                  <p className="text-orange-400 text-xs mb-2 font-heading uppercase tracking-wider">Subir archivo encriptado (.mmf)</p>
                  <input type="file" className="hidden" id="enc-file" accept=".mmf,.MMF,.slv,.SLV,.enc"
                    onChange={async (e) => { const encFile = e.target.files?.[0]; if (!encFile) return; const fd = new FormData(); fd.append('file', encFile); fd.append('notes', `Encriptado manual para SLAVE ${ticket.slave_machine_sn || ''}`); try { toast.info('Subiendo...'); await axios.post(`${API}/admin/tickets/${ticketId}/upload-encrypted`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }); toast.success('Archivo encriptado subido'); fetchTicket(); } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir'); } e.target.value = ''; }} />
                  <Button type="button" variant="outline" size="sm" className="w-full border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                    onClick={() => document.getElementById('enc-file').click()}>
                    <Lock className="w-3 h-3 mr-2" />Subir .MMF encriptado
                  </Button>
                </div>
              )}
            </div>

            {/* Save to Solutions */}
            {(ticket.status === 'delivered' || ticket.status === 'closed') && modifieds.length > 0 && (originals.length > 0 || originalDecrypted.length > 0) && (
              <SaveToSolutionsPanel ticket={ticket} token={token} originals={originals} originalDecrypted={originalDecrypted} modifieds={modifieds} />
            )}
          </div>

          {/* Right Panel */}
          <div className="space-y-4">
            {/* Client Info */}
            <div className="bg-surface border border-white/10 rounded-md p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-zinc-400" />
                <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider">Cliente</h3>
              </div>
              <p className="text-white text-sm">{ticket.user_company}</p>
              <p className="text-zinc-500 text-xs">{ticket.user_email}</p>
            </div>

            {/* Chat - Position 2 (right after client info) */}
            <div className="bg-surface border border-white/10 rounded-md">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-heading text-white font-bold text-sm">CHAT CON CLIENTE</h3>
              </div>
              <div ref={chatRef} className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
                {(!ticket.messages || ticket.messages.length === 0) ? (
                  <p className="text-zinc-600 text-sm text-center py-8">Sin mensajes</p>
                ) : ticket.messages.map(m => (
                  <div key={m.id} className={`flex ${m.is_admin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] rounded-lg p-3 ${m.is_admin ? 'bg-primary/10' : 'bg-zinc-800'}`}>
                      <p className={`text-xs mb-1 ${m.is_admin ? 'text-primary' : 'text-blue-400'}`}>{m.sender_name}{m.is_admin && ' (Tú)'}</p>
                      <p className="text-white text-sm whitespace-pre-wrap">{m.message}</p>
                      {m.attachment && (
                        <button onClick={async () => { try { const res = await axios.get(`${API}/tickets/${ticketId}/attachment/${m.attachment.file_id}`, { headers, responseType: 'blob', timeout: 120000 }); const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = m.attachment.filename; a.click(); URL.revokeObjectURL(url); } catch { toast.error('Error al descargar adjunto'); } }}
                          className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline">
                          <Paperclip className="w-3 h-3" />{m.attachment.filename}
                        </button>
                      )}
                      <p className="text-zinc-600 text-[10px] mt-1">{new Date(m.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Responder al cliente..." rows={2}
                    className="bg-surface-highlight border-white/10 text-white flex-1 min-h-[44px] max-h-28 text-sm" />
                  <div className="flex flex-col gap-1">
                    <input type="file" className="hidden" id="admin-chat-attach" onChange={e => setAttachment(e.target.files?.[0])} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => document.getElementById('admin-chat-attach').click()}
                      className="text-zinc-500 hover:text-white h-9 w-9"><Paperclip className="w-4 h-4" /></Button>
                    <Button type="submit" disabled={sending || (!message.trim() && !attachment)} className="bg-primary hover:bg-primary/90 h-9 w-9" size="icon">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                {attachment && <p className="text-xs text-primary mt-1">Adjunto: {attachment.name}</p>}
              </form>
            </div>

            {/* Status */}
            <div className="bg-surface border border-white/10 rounded-md p-4">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Estado</h3>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-surface-highlight border-white/10 text-white mb-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  {Object.entries(STATES).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="text-white hover:bg-white/10">{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={updateStatus} disabled={newStatus === ticket.status} size="sm" className="w-full bg-primary hover:bg-primary/90">
                Cambiar Estado
              </Button>
            </div>

            {/* Priority */}
            <div className="bg-surface border border-white/10 rounded-md p-4">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Prioridad</h3>
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger className="bg-surface-highlight border-white/10 text-white mb-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  {Object.entries(PRIORITIES).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="text-white hover:bg-white/10">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={updatePriority} disabled={newPriority === ticket.priority} size="sm" className="w-full" variant="outline">
                Actualizar Prioridad
              </Button>
            </div>

            {/* Reading Type (Master/Slave) */}
            <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="reading-type-panel">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Tipo Lectura</h3>
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={readingType === 'master' ? 'default' : 'outline'}
                  className={readingType === 'master' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'border-white/10 text-white flex-1'}
                  onClick={() => setReadingType('master')}
                  data-testid="reading-type-master-btn"
                >
                  <Unlock className="w-3 h-3 mr-1" /> Master
                </Button>
                <Button
                  size="sm"
                  variant={readingType === 'slave' ? 'default' : 'outline'}
                  className={readingType === 'slave' ? 'bg-orange-600 hover:bg-orange-700 flex-1' : 'border-white/10 text-white flex-1'}
                  onClick={() => setReadingType('slave')}
                  data-testid="reading-type-slave-btn"
                >
                  <Lock className="w-3 h-3 mr-1" /> Slave
                </Button>
              </div>
              {readingType === 'slave' && (
                <div className="mb-2">
                  {slaveTools.length > 0 ? (
                    <Select value={slaveSn} onValueChange={setSlaveSn}>
                      <SelectTrigger className="bg-surface-highlight border-white/10 text-white text-xs">
                        <SelectValue placeholder="Seleccionar herramienta SLAVE" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-white/10">
                        {slaveTools.map(t => (
                          <SelectItem key={t.machine_sn} value={t.machine_sn} className="text-white hover:bg-white/10 text-xs">
                            {t.label} ({t.machine_sn})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={slaveSn} onChange={e => setSlaveSn(e.target.value)}
                      placeholder="Nº serie SLAVE (machine_sn)"
                      className="bg-surface-highlight border-white/10 text-white text-xs"
                      data-testid="slave-sn-input"
                    />
                  )}
                </div>
              )}
              <Button
                onClick={updateReadingType}
                disabled={updatingReadingType || (readingType === (ticket.reading_type || 'master') && slaveSn === (ticket.slave_machine_sn || ''))}
                size="sm" className="w-full bg-primary hover:bg-primary/90"
                data-testid="update-reading-type-btn"
              >
                {updatingReadingType ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Guardar Tipo
              </Button>
              {ticket.reading_type === 'slave' && ticket.slave_machine_sn && (
                <p className="text-orange-400 text-[10px] mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> SLAVE: {ticket.slave_machine_sn} — El archivo se auto-encripta al subir
                </p>
              )}
            </div>

            {/* Price */}
            <div className="bg-surface border border-white/10 rounded-md p-4">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Precio</h3>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="Precio en €" type="number" step="0.01"
                    className="bg-surface-highlight border-white/10 text-white" />
                  <span className="text-zinc-400 self-center">€</span>
                </div>
                <Input value={estimatedTime} onChange={e => setEstimatedTime(e.target.value)} placeholder="Tiempo estimado (ej: 1h)"
                  className="bg-surface-highlight border-white/10 text-white" />
                <Button onClick={updatePrice} size="sm" className="w-full bg-primary hover:bg-primary/90">
                  Fijar Precio
                </Button>
              </div>
              {ticket.payment_status && (
                <div className="mt-3 pt-3 border-t border-white/10 text-xs">
                  <span className="text-zinc-500">Pago: </span>
                  <span className={isPaid ? 'text-green-400' : 'text-yellow-400'}>
                    {isPaid ? 'Pagado' : ticket.payment_status}
                  </span>
                  {ticket.credits_applied > 0 && <span className="text-zinc-500 ml-2">({ticket.credits_applied} créditos)</span>}
                  {ticket.euros_paid > 0 && <span className="text-zinc-500 ml-2">(+{ticket.euros_paid}€)</span>}
                </div>
              )}
            </div>

            {/* AI Suggestions */}
            <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="ai-suggestions-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-blue-400" />
                  <h3 className="font-heading text-sm text-blue-400 uppercase tracking-wider">Sugerencias IA</h3>
                </div>
              </div>
              <Button
                onClick={fetchAiSuggestions}
                disabled={loadingSuggestions}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700 mb-3"
                data-testid="fetch-ai-suggestions-btn"
              >
                {loadingSuggestions ? (
                  <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Analizando...</>
                ) : (
                  <><Brain className="w-3 h-3 mr-1" /> Buscar soluciones con IA</>
                )}
              </Button>

              {aiSuggestions && (
                <div className="space-y-2">
                  {/* Analysis info */}
                  {aiSuggestions.analysis && (
                    <div className="text-xs space-y-1 pb-2 border-b border-white/10">
                      {aiSuggestions.analysis.hw_id && <div><span className="text-zinc-500">HW: </span><span className="text-white font-mono">{aiSuggestions.analysis.hw_id}</span></div>}
                      {aiSuggestions.analysis.sw_id && <div><span className="text-zinc-500">SW: </span><span className="text-white font-mono">{aiSuggestions.analysis.sw_id}</span></div>}
                      {aiSuggestions.analysis.ecu_family && <div><span className="text-zinc-500">ECU: </span><span className="text-white">{aiSuggestions.analysis.ecu_family}</span></div>}
                      {aiSuggestions.analysis.summary && <p className="text-zinc-400 italic">{aiSuggestions.analysis.summary}</p>}
                    </div>
                  )}

                  {/* Suggestions */}
                  {aiSuggestions.suggestions?.length > 0 ? (
                    aiSuggestions.suggestions.map((sug, i) => (
                      <div key={i} className="bg-surface-highlight rounded p-2.5 border border-white/5" data-testid={`ai-suggestion-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-xs font-medium">{sug.vehicle}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            sug.confidence >= 0.8 ? 'bg-green-500/20 text-green-400' :
                            sug.confidence >= 0.6 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {Math.round(sug.confidence * 100)}%
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mb-1">
                          {sug.ecu_type} {sug.engine_code && `- ${sug.engine_code}`} | {sug.match_type}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {sug.solutions_available?.slice(0, 4).map((name, j) => (
                            <span key={j} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">{name}</span>
                          ))}
                        </div>
                        <div className="text-[10px] text-zinc-600 mt-1">{sug.versions_count} versión(es)</div>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-500 text-xs text-center py-2">{aiSuggestions.message}</p>
                  )}
                </div>
              )}
            </div>

            {/* Audit Log */}
            {ticket.audit_logs?.length > 0 && (
              <div className="bg-surface border border-white/10 rounded-md p-4">
                <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Historial</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {ticket.audit_logs.map((log, i) => (
                    <div key={i} className="text-xs">
                      <p className="text-zinc-300">{log.details || log.action}</p>
                      <p className="text-zinc-600">{new Date(log.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminTicketDetailPage;
